import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';

import FileAPI from 'services/api/file-updated.api';
import FileDropZone from 'components/Shared/FileDropZone/FileDropZone';
import { showSnackbar } from 'store/snackbar/snackbar.action';
import { useAppSelector } from 'hooks/reduxHooks';

type TFlowFileUploadProps = {
  allowedTypes?: string[];
  showDownloadSampleFile?: boolean;
};

const CSV_UPLOAD_FILE_LIMIT = 200099999;

const FlowFileUpload: React.FC<TFlowFileUploadProps> = ({ allowedTypes, showDownloadSampleFile = false }) => {
  const fileApi = useMemo(() => new FileAPI(), []);

  const [isBusy, setIsBusy] = useState(false);
  const [totalFiles, setTotalFiles] = useState(0);
  const [filesUploaded, setFilesUploaded] = useState(0);
  const [fileUploadedPercentage, setFileUploadedPercentage] = useState(0);
  const filesUUID = useAppSelector((state) => state.te.journey.metadata?.filesUUID || '');
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const uploadFiles = async (files: FileList) => {
    const batchTotal = files.length;
    setFilesUploaded(0);
    setTotalFiles(batchTotal);
    setIsBusy(true);

    const filePromises: Promise<unknown>[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';

      if (file.size > CSV_UPLOAD_FILE_LIMIT) {
        dispatch(
          showSnackbar({
            content: `The file "${files[i].name}" is too large. The maximum file size is 200MB.`,
            type: 'error',
          }),
        );
      } else if (!isCsv) {
        dispatch(
          showSnackbar({
            content: `The file "${files[i].name}" is not a valid CSV file.`,
            type: 'error',
          }),
        );
      } else {
        const promise = fileApi.uploadViaPreSignedUrl(files[i], setFileUploadedPercentage, filesUUID);

        promise.then(() => {
          setFilesUploaded((oldState) => oldState + 1);
        });

        filePromises.push(promise);
      }
    }

    if (filePromises.length) {
      const rejected = (await Promise.allSettled(filePromises)).filter((r) => r.status === 'rejected');

      if (rejected.length) {
        const firstReason = (rejected[0] as PromiseRejectedResult).reason;
        dispatch(
          showSnackbar({
            content: `${
              batchTotal > 1 ? 'Some of your files did not upload successfully.' : 'The file failed to upload.'
            } ${firstReason?.message ?? ''}`.trim(),
            type: 'error',
          }),
        );
      } else {
        const uploadedCount = filePromises.length;
        const message =
          uploadedCount > 1 && uploadedCount === batchTotal
            ? 'All files have been successfully uploaded.'
            : uploadedCount > 1
              ? `${uploadedCount} files have been successfully uploaded.`
              : 'The file has been uploaded successfully.';

        dispatch(
          showSnackbar({
            content: message,
            type: 'success',
          }),
        );
      }
    }

    await queryClient.invalidateQueries(['ctx-data-file']);
    setIsBusy(false);
  };

  return (
    <FileDropZone
      allowedTypes={allowedTypes}
      showDownloadSampleFile={showDownloadSampleFile}
      onFilesSelected={uploadFiles}
      isBusy={isBusy}
      totalFiles={totalFiles}
      filesUploaded={filesUploaded}
      fileUploadedPercentage={fileUploadedPercentage}
    />
  );
};

export default FlowFileUpload;
